/**
 * MODULE: domain/pages/frontend/utils/blockCommentUtils.ts
 * PURPOSE: Helpers for inline block comment threads
 */

import type { PageComment } from '../../contracts/pages.types';
import type { DraggableBlockTarget } from './pagesBlockDragTarget';
import {
  buildCommentThreads,
  countOpenThreads,
  filterThreadsByResolvedVisibility,
} from './pageCommentThreadUtils';

export function resolvePagesBlockIdFromTarget(target: DraggableBlockTarget): string | null {
  const raw = target.node.attrs?.pagesBlockId;
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
}

export function filterCommentsForBlock(comments: PageComment[], blockId: string): PageComment[] {
  return comments.filter((comment) => comment.block_id === blockId);
}

export function filterPageLevelComments(comments: PageComment[]): PageComment[] {
  return comments.filter((comment) => !comment.block_id);
}

export function countBlockComments(comments: PageComment[], blockId: string): number {
  return filterCommentsForBlock(comments, blockId).length;
}

export function countOpenBlockComments(comments: PageComment[], blockId: string): number {
  return countOpenThreads(buildCommentThreads(filterCommentsForBlock(comments, blockId)));
}

export function buildOpenBlockCommentCounts(comments: PageComment[]): Record<string, number> {
  const byBlock = new Map<string, PageComment[]>();
  for (const comment of comments) {
    if (!comment.block_id) {
      continue;
    }
    const existing = byBlock.get(comment.block_id) ?? [];
    existing.push(comment);
    byBlock.set(comment.block_id, existing);
  }

  const counts: Record<string, number> = {};
  for (const [blockId, blockComments] of byBlock) {
    const openThreads = countOpenThreads(buildCommentThreads(blockComments));
    if (openThreads > 0) {
      counts[blockId] = openThreads;
    }
  }
  return counts;
}

export function isCommentResolved(comment: PageComment): boolean {
  return Boolean(comment.resolved_at);
}

export function countOpenComments(comments: PageComment[]): number {
  return countOpenThreads(buildCommentThreads(comments));
}

export function filterCommentsByResolvedVisibility(
  comments: PageComment[],
  showResolved: boolean
): PageComment[] {
  if (showResolved) return comments;
  const openThreadRoots = new Set(
    filterThreadsByResolvedVisibility(buildCommentThreads(comments), false).map(
      (thread) => thread.root.id
    )
  );
  return comments.filter(
    (comment) =>
      openThreadRoots.has(comment.id) ||
      (comment.parent_comment_id != null && openThreadRoots.has(comment.parent_comment_id))
  );
}
